import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { api, productImage, CATEGORY_META } from "../lib/api";
import { Plus, Trash2, Pencil, LogOut, Loader2, Upload, FileText, X, ShoppingBag, Tv, Phone, MapPin, Check, Video, Send, CalendarDays, Clock, Mail } from "lucide-react";

const EMPTY = { category: "iphone", title: "", subtitle: "", description: "", features: "", price: 795,
  pdf_path: null, pdf_filename: null, sample_images: [], is_active: true };

export default function AdminPanel() {
  const { admin, logout, loading } = useAuth();
  const navigate = useNavigate();
  const [products, setProducts] = useState([]);
  const [orders, setOrders] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [appointments, setAppointments] = useState([]);
  const [orderEditing, setOrderEditing] = useState(null);
  const [orderForm, setOrderForm] = useState({ chapters: [], pdf_path: null, pdf_filename: "" });
  const [orderBusy, setOrderBusy] = useState("");
  const [pw, setPw] = useState({ next: "", confirm: "" });
  const [pwMsg, setPwMsg] = useState("");
  const [pwErr, setPwErr] = useState("");
  const [pwBusy, setPwBusy] = useState(false);
  const [tab, setTab] = useState("products");
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState("");
  const [msg, setMsg] = useState("");

  useEffect(() => {
    if (!loading && !admin) navigate("/admin");
  }, [admin, loading, navigate]);

  const load = () => {
    api.get("/admin/products").then((r) => setProducts(r.data));
    api.get("/admin/orders").then((r) => setOrders(r.data));
    api.get("/admin/bookings").then((r) => setBookings(r.data));
    api.get("/admin/appointments").then((r) => setAppointments(r.data));
  };
  useEffect(() => { if (admin) load(); }, [admin]);

  const openNew = () => { setForm(EMPTY); setEditing("new"); setMsg(""); };
  const openEdit = (p) => {
    setForm({ ...p, features: (p.features || []).join("\n") });
    setEditing(p.id); setMsg("");
  };

  const uploadFile = async (file, kind) => {
    setUploading(kind);
    const fd = new FormData();
    fd.append("file", file);
    const res = await api.post("/admin/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
    setUploading("");
    return res.data;
  };

  const handlePdf = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const data = await uploadFile(file, "pdf");
    setForm((f) => ({ ...f, pdf_path: data.path, pdf_filename: data.filename }));
  };

  const handleImages = async (e) => {
    const files = Array.from(e.target.files);
    for (const file of files) {
      const data = await uploadFile(file, "img");
      setForm((f) => ({ ...f, sample_images: [...f.sample_images, data.path] }));
    }
  };

  const removeImage = (path) => setForm((f) => ({ ...f, sample_images: f.sample_images.filter((p) => p !== path) }));

  const save = async () => {
    setSaving(true);
    const payload = {
      category: form.category, title: form.title, subtitle: form.subtitle,
      description: form.description,
      features: form.features.split("\n").map((s) => s.trim()).filter(Boolean),
      price: parseFloat(form.price), pdf_path: form.pdf_path, pdf_filename: form.pdf_filename,
      sample_images: form.sample_images, is_active: form.is_active !== false,
    };
    try {
      if (editing === "new") await api.post("/admin/products", payload);
      else await api.put(`/admin/products/${editing}`, payload);
      setEditing(null);
      load();
    } catch (e) {
      setMsg(e.response?.data?.detail || "Kunde inte spara.");
    } finally { setSaving(false); }
  };

  const remove = async (id) => {
    if (!window.confirm("Ta bort denna manual?")) return;
    await api.delete(`/admin/products/${id}`);
    load();
  };

  const setBookingStatus = async (id, status) => {
    await api.patch(`/admin/bookings/${id}`, { status });
    load();
  };

  const cancelAppointment = async (id) => {
    if (!window.confirm("Avboka den här tiden? Kunden meddelas inte automatiskt.")) return;
    await api.patch(`/admin/appointments/${id}/cancel`);
    load();
  };

  const openOrderEditor = (o) => {
    setOrderForm({ chapters: o.chapters || [], pdf_path: o.pdf_path || null, pdf_filename: o.pdf_path ? (o.pdf_filename || "") : "" });
    setOrderEditing(o.session_id);
  };
  const uploadOrderFile = async (file) => {
    const fd = new FormData();
    fd.append("file", file);
    const res = await api.post("/admin/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
    return res.data;
  };
  const addChapterVideo = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setOrderBusy("video");
    const data = await uploadOrderFile(file);
    setOrderForm((f) => ({ ...f, chapters: [...f.chapters, { title: `Kapitel ${f.chapters.length + 1}`, path: data.path }] }));
    setOrderBusy("");
    e.target.value = "";
  };
  const setChapterTitle = (i, title) => setOrderForm((f) => ({ ...f, chapters: f.chapters.map((c, idx) => (idx === i ? { ...c, title } : c)) }));
  const removeChapter = (i) => setOrderForm((f) => ({ ...f, chapters: f.chapters.filter((_, idx) => idx !== i) }));
  const uploadOrderPdf = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setOrderBusy("pdf");
    const data = await uploadOrderFile(file);
    setOrderForm((f) => ({ ...f, pdf_path: data.path, pdf_filename: data.filename }));
    setOrderBusy("");
    e.target.value = "";
  };
  const saveOrder = async (markReady) => {    setOrderBusy("save");
    try {
      await api.patch(`/admin/orders/${orderEditing}`, {
        chapters: orderForm.chapters, pdf_path: orderForm.pdf_path,
        pdf_filename: orderForm.pdf_filename, mark_ready: markReady,
      });
      setOrderEditing(null);
      load();
    } catch (err) {
      alert(err.response?.data?.detail || "Kunde inte spara.");
    } finally {
      setOrderBusy("");
    }
  };

  const changePassword = async (e) => {
    e.preventDefault();
    setPwMsg(""); setPwErr("");
    if (!pw.next || !pw.confirm) { setPwErr("Fyll i båda fälten."); return; }
    if (pw.next !== pw.confirm) { setPwErr("Lösenorden matchar inte."); return; }
    if (pw.next.length < 6) { setPwErr("Lösenordet måste vara minst 6 tecken."); return; }
    setPwBusy(true);
    try {
      await api.post("/auth/change-password", { new_password: pw.next });
      setPw({ next: "", confirm: "" });
      setPwMsg("Lösenordet har uppdaterats!");
    } catch (err) {
      setPwErr(err.response?.data?.detail || "Kunde inte uppdatera lösenordet.");
    } finally {
      setPwBusy(false);
    }
  };

  if (loading || !admin) return <div className="p-16 text-center"><Loader2 className="animate-spin mx-auto" size={40} /></div>;

  return (
    <div className="max-w-6xl mx-auto px-6 md:px-10 py-10">
      <div className="flex items-center justify-between flex-wrap gap-4">
        <h1 className="font-heading text-4xl font-extrabold text-[#1A1A1A]">Administration</h1>
        <button data-testid="admin-logout" onClick={() => { logout(); navigate("/"); }}
          className="inline-flex items-center gap-2 min-h-[52px] px-5 border-2 border-gray-400 rounded-lg text-lg font-semibold hover:bg-gray-100">
          <LogOut size={22} /> Logga ut
        </button>
      </div>

      <div className="flex gap-3 mt-6 border-b-2 border-gray-200">
        {[["products", "Manualer"], ["orders", "Beställningar"], ["bookings", "Bokningar"], ["appointments", "Tidsbokningar"], ["settings", "Konto"]].map(([k, label]) => (
          <button key={k} data-testid={`tab-${k}`} onClick={() => setTab(k)}
            className={`px-5 py-3 text-lg font-bold border-b-4 -mb-0.5 transition-colors ${tab === k ? "border-[#0056D2] text-[#0056D2]" : "border-transparent text-[#4A4A4A]"}`}>
            {label}
          </button>
        ))}
      </div>

      {tab === "products" && (
        <div className="mt-8">
          <button data-testid="add-product-btn" onClick={openNew}
            className="inline-flex items-center gap-2 min-h-[60px] px-6 bg-[#0056D2] text-white text-lg font-bold rounded-lg hover:bg-[#003E99]">
            <Plus size={24} strokeWidth={2.5} /> Lägg till manual
          </button>

          <div className="grid md:grid-cols-2 gap-5 mt-6">
            {products.map((p) => (
              <div key={p.id} data-testid={`admin-product-${p.id}`} className={`bg-white border-2 rounded-xl p-5 flex gap-4 ${p.is_active ? "border-gray-300" : "border-gray-200 opacity-60"}`}>
                {productImage(p) && <img src={productImage(p)} alt="" className="w-20 h-20 object-cover rounded-lg border" />}
                <div className="flex-1">
                  <p className="text-sm font-semibold text-[#0056D2] uppercase">{CATEGORY_META[p.category]?.name}</p>
                  <p className="font-heading text-xl font-bold text-[#1A1A1A]">{p.title}</p>
                  <p className="text-base text-[#4A4A4A]">{Math.round(p.price)} kr {p.pdf_path ? "· PDF ✓" : "· ingen PDF"} {p.is_active ? "" : "· dold"}</p>
                  <div className="flex gap-2 mt-3">
                    <button data-testid={`edit-${p.id}`} onClick={() => openEdit(p)} className="inline-flex items-center gap-1 px-4 py-2 border-2 border-gray-400 rounded-lg font-semibold hover:bg-gray-100"><Pencil size={18} /> Ändra</button>
                    <button data-testid={`delete-${p.id}`} onClick={() => remove(p.id)} className="inline-flex items-center gap-1 px-4 py-2 border-2 border-red-300 text-[#D32F2F] rounded-lg font-semibold hover:bg-red-50"><Trash2 size={18} /> Ta bort</button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {tab === "orders" && (
        <div className="mt-8">
          {orders.length === 0 && <p className="text-lg text-[#4A4A4A]">Inga beställningar ännu.</p>}
          <div className="space-y-3">
            {orders.map((o) => (
              <div key={o.id} data-testid={`order-${o.id}`} className="bg-white border-2 border-gray-300 rounded-xl p-5 flex items-center gap-4 flex-wrap">
                {o.is_digital ? <Video className="text-[#0056D2] shrink-0" size={28} /> : <ShoppingBag className="text-[#0B7A3F] shrink-0" size={28} />}
                <div className="flex-1 min-w-[200px]">
                  <p className="font-heading text-lg font-bold text-[#1A1A1A]">{o.product_title}</p>
                  <p className="text-base text-[#4A4A4A]">{o.email} · {new Date(o.created_at).toLocaleString("sv-SE")}</p>
                  {o.is_digital && (
                    <span className={`inline-block mt-2 px-3 py-1 rounded-full text-sm font-bold ${o.fulfillment_status === "ready" ? "bg-green-50 text-[#0B7A3F]" : "bg-amber-50 text-[#B38600]"}`}>
                      {o.fulfillment_status === "ready" ? "Material klart – kunden har åtkomst" : "Material förbereds"}
                    </span>
                  )}
                </div>
                <p className="font-heading text-xl font-bold">{Math.round(o.amount)} kr</p>
                {o.is_digital && (
                  <button data-testid={`prepare-${o.id}`} onClick={() => openOrderEditor(o)}
                    className="inline-flex items-center gap-2 px-4 py-3 bg-[#0056D2] text-white rounded-lg font-semibold hover:bg-[#003E99]">
                    <Upload size={18} /> Förbered innehåll
                  </button>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {tab === "bookings" && (
        <div className="mt-8">
          {bookings.length === 0 && <p className="text-lg text-[#4A4A4A]">Inga bokningar ännu.</p>}
          <div className="space-y-3">
            {bookings.map((b) => (
              <div key={b.id} data-testid={`booking-${b.id}`} className="bg-white border-2 border-gray-300 rounded-xl p-5">
                <div className="flex items-start justify-between gap-4 flex-wrap">
                  <div className="flex items-start gap-4">
                    <Tv className="text-[#0056D2] shrink-0 mt-1" size={28} />
                    <div>
                      <p className="font-heading text-xl font-bold text-[#1A1A1A]">{b.name}</p>
                      <p className="flex items-center gap-2 text-base text-[#4A4A4A] mt-1"><Phone size={18} /> {b.phone}</p>
                      <p className="flex items-center gap-2 text-base text-[#4A4A4A]"><MapPin size={18} /> {b.postal_code}</p>
                      <p className="text-base text-[#4A4A4A] mt-1">
                        Tjänst: <strong className="text-[#1A1A1A]">{b.service === "smart_tv" ? "Hemkurs Smart-TV" : b.service === "tv_box" ? "Hemkurs TV-box" : b.service === "sakerhet_hembesok" ? "Säker på nätet – Hembesök" : "ej angivet"}</strong>
                      </p>
                      {b.service === "smart_tv" && (
                        <p className="text-base text-[#4A4A4A]">Smart-TV-märke: <strong className="text-[#1A1A1A]">{b.tv_brand || "ej angivet"}</strong></p>
                      )}
                      {b.service === "tv_box" && (
                        <p className="text-base text-[#4A4A4A]">TV-leverantör: <strong className="text-[#1A1A1A]">{(b.tv_providers || []).join(", ") || "ej angivet"}</strong></p>
                      )}
                      <p className="text-sm text-[#4A4A4A] mt-1">{new Date(b.created_at).toLocaleString("sv-SE")}</p>
                    </div>
                  </div>
                  <div className="flex flex-col items-end gap-2">
                    <span className={`px-3 py-1 rounded-full text-sm font-bold ${b.status === "klar" ? "bg-gray-100 text-gray-600" : b.status === "kontaktad" ? "bg-green-50 text-[#0B7A3F]" : "bg-amber-50 text-[#B38600]"}`}>
                      {b.status === "klar" ? "Klar" : b.status === "kontaktad" ? "Kontaktad" : "Ny"}
                    </span>
                    <div className="flex gap-2">
                      {b.status === "ny" && (
                        <button data-testid={`booking-contacted-${b.id}`} onClick={() => setBookingStatus(b.id, "kontaktad")}
                          className="inline-flex items-center gap-1 px-3 py-2 border-2 border-gray-400 rounded-lg text-base font-semibold hover:bg-gray-100"><Check size={16} /> Markera kontaktad</button>
                      )}
                      {b.status === "kontaktad" && (
                        <button data-testid={`booking-done-${b.id}`} onClick={() => setBookingStatus(b.id, "klar")}
                          className="inline-flex items-center gap-1 px-3 py-2 border-2 border-gray-400 rounded-lg text-base font-semibold hover:bg-gray-100"><Check size={16} /> Markera klar</button>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {tab === "appointments" && (
        <div className="mt-8">
          {appointments.length === 0 && <p className="text-lg text-[#4A4A4A]">Inga tidsbokningar ännu.</p>}
          <div className="space-y-3">
            {appointments.map((a) => (
              <div key={a.id} data-testid={`appointment-${a.id}`} className={`bg-white border-2 rounded-xl p-5 ${a.status === "avbokad" ? "border-gray-200 opacity-60" : "border-gray-300"}`}>
                <div className="flex items-start justify-between gap-4 flex-wrap">
                  <div className="flex items-start gap-4">
                    <CalendarDays className="text-[#0056D2] shrink-0 mt-1" size={28} />
                    <div>
                      <p className="font-heading text-xl font-bold text-[#1A1A1A]">{a.service_label}</p>
                      <p className="flex items-center gap-2 text-base text-[#1A1A1A] mt-1 capitalize">
                        <Clock size={18} /> {new Date(a.date + "T00:00:00").toLocaleDateString("sv-SE", { weekday: "long", day: "numeric", month: "long" })}, kl {a.start}–{a.end}
                      </p>
                      <p className="text-base text-[#4A4A4A] mt-1">{a.name}</p>
                      <p className="flex items-center gap-2 text-base text-[#4A4A4A]"><Phone size={18} /> {a.phone}</p>
                      <p className="flex items-center gap-2 text-base text-[#4A4A4A]"><Mail size={18} /> {a.email}</p>
                    </div>
                  </div>
                  <div className="flex flex-col items-end gap-2">
                    <span className={`px-3 py-1 rounded-full text-sm font-bold ${a.status === "avbokad" ? "bg-red-50 text-[#D32F2F]" : "bg-green-50 text-[#0B7A3F]"}`}>
                      {a.status === "avbokad" ? "Avbokad" : "Bokad"}
                    </span>
                    {a.status === "bokad" && (
                      <button data-testid={`appointment-cancel-${a.id}`} onClick={() => cancelAppointment(a.id)}
                        className="inline-flex items-center gap-1 px-3 py-2 border-2 border-red-300 text-[#D32F2F] rounded-lg text-base font-semibold hover:bg-red-50"><X size={16} /> Avboka</button>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {tab === "settings" && (
        <div className="mt-8 max-w-md">
          <div className="bg-white border-2 border-gray-300 rounded-xl p-6 md:p-8">
            <h2 className="font-heading text-2xl font-bold text-[#1A1A1A]">Byt lösenord</h2>
            <p className="text-base text-[#4A4A4A] mt-2">Inloggad som <strong className="text-[#1A1A1A]">{admin.email}</strong>.</p>
            <form onSubmit={changePassword} className="mt-5 space-y-4">
              <div>
                <label className="text-lg font-bold mb-1 block text-[#1A1A1A]">Nytt lösenord</label>
                <input type="password" data-testid="new-password" value={pw.next} onChange={(e) => setPw({ ...pw, next: e.target.value })}
                  className="min-h-[52px] text-lg px-4 border-2 border-gray-400 rounded-lg w-full focus:border-[#0056D2]" />
              </div>
              <div>
                <label className="text-lg font-bold mb-1 block text-[#1A1A1A]">Bekräfta nytt lösenord</label>
                <input type="password" data-testid="confirm-password" value={pw.confirm} onChange={(e) => setPw({ ...pw, confirm: e.target.value })}
                  className="min-h-[52px] text-lg px-4 border-2 border-gray-400 rounded-lg w-full focus:border-[#0056D2]" />
              </div>
              {pwErr && <p data-testid="pw-error" className="flex items-center gap-2 text-[#D32F2F] font-semibold"><X size={20} /> {pwErr}</p>}
              {pwMsg && <p data-testid="pw-success" className="flex items-center gap-2 text-[#0B7A3F] font-semibold"><Check size={20} /> {pwMsg}</p>}
              <button type="submit" data-testid="update-password-btn" disabled={pwBusy}
                className="inline-flex items-center justify-center gap-2 min-h-[56px] w-full bg-[#0056D2] text-white text-lg font-bold rounded-lg hover:bg-[#003E99] transition-colors disabled:opacity-60">
                {pwBusy ? <Loader2 className="animate-spin" size={22} /> : null} Uppdatera lösenord
              </button>
            </form>
          </div>
        </div>
      )}

      {/* Digital order content editor */}
      {orderEditing && (
        <div className="fixed inset-0 bg-black/40 flex items-start justify-center p-4 overflow-y-auto z-50" data-testid="order-editor">
          <div className="bg-white rounded-xl max-w-2xl w-full my-8 p-7">
            <div className="flex items-center justify-between">
              <h2 className="font-heading text-2xl font-bold">Förbered kursmaterial</h2>
              <button data-testid="close-order-editor" onClick={() => setOrderEditing(null)} className="p-2 hover:bg-gray-100 rounded-lg"><X size={26} /></button>
            </div>
            <p className="text-base text-[#4A4A4A] mt-1">Ladda upp videokapitel och en PDF-guide för den här kunden. När du markerar som klar får kunden åtkomst och en länk mejlas.</p>

            <div className="mt-5 space-y-5">
              <div>
                <label className="text-base font-bold block mb-2">Videokapitel</label>
                <div className="space-y-3">
                  {orderForm.chapters.map((c, i) => (
                    <div key={i} className="flex items-center gap-2 border-2 border-gray-200 rounded-lg p-2">
                      <Video size={22} className="text-[#0056D2] shrink-0" />
                      <input data-testid={`chapter-title-${i}`} value={c.title} onChange={(e) => setChapterTitle(i, e.target.value)} className="flex-1 min-h-[44px] px-3 border-2 border-gray-300 rounded-lg" />
                      <button onClick={() => removeChapter(i)} className="p-2 text-[#D32F2F] hover:bg-red-50 rounded-lg"><Trash2 size={18} /></button>
                    </div>
                  ))}
                  {orderForm.chapters.length === 0 && <p className="text-base text-[#4A4A4A]">Inga videokapitel tillagda ännu.</p>}
                </div>
                <label className="inline-flex items-center gap-2 px-4 py-3 mt-3 border-2 border-gray-400 rounded-lg cursor-pointer hover:bg-gray-100 font-semibold">
                  {orderBusy === "video" ? <Loader2 className="animate-spin" size={20} /> : <Upload size={20} />} Lägg till videokapitel
                  <input data-testid="order-video" type="file" accept="video/*" onChange={addChapterVideo} className="hidden" />
                </label>
              </div>

              <div>
                <label className="text-base font-bold block mb-2">PDF-guide</label>
                <label className="inline-flex items-center gap-2 px-4 py-3 border-2 border-gray-400 rounded-lg cursor-pointer hover:bg-gray-100 font-semibold">
                  {orderBusy === "pdf" ? <Loader2 className="animate-spin" size={20} /> : <Upload size={20} />} Ladda upp PDF
                  <input data-testid="order-pdf" type="file" accept="application/pdf" onChange={uploadOrderPdf} className="hidden" />
                </label>
                {orderForm.pdf_filename && <p className="flex items-center gap-2 text-base text-[#0B7A3F] mt-2"><FileText size={18} /> {orderForm.pdf_filename}</p>}
              </div>

              <div className="flex gap-3 pt-2 flex-wrap">
                <button data-testid="save-order-draft" onClick={() => saveOrder(false)} disabled={orderBusy === "save"}
                  className="inline-flex items-center gap-2 min-h-[52px] px-5 border-2 border-gray-400 rounded-lg font-semibold hover:bg-gray-100 disabled:opacity-50">
                  {orderBusy === "save" ? <Loader2 className="animate-spin" size={20} /> : null} Spara utkast
                </button>
                <button data-testid="save-order-ready" onClick={() => saveOrder(true)} disabled={orderBusy === "save"}
                  className="inline-flex items-center gap-2 min-h-[52px] px-5 bg-[#0056D2] text-white rounded-lg font-bold hover:bg-[#003E99] disabled:opacity-50">
                  <Send size={18} /> Markera som klar &amp; skicka länk
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Editor */}
      {editing && (
        <div className="fixed inset-0 bg-black/40 flex items-start justify-center p-4 overflow-y-auto z-50" data-testid="product-editor">
          <div className="bg-white rounded-xl max-w-2xl w-full my-8 p-7">
            <div className="flex items-center justify-between">
              <h2 className="font-heading text-2xl font-bold">{editing === "new" ? "Ny manual" : "Ändra manual"}</h2>
              <button data-testid="close-editor" onClick={() => setEditing(null)} className="p-2 hover:bg-gray-100 rounded-lg"><X size={26} /></button>
            </div>

            <div className="space-y-4 mt-5">
              <div>
                <label className="text-base font-bold block mb-1">Kategori</label>
                <select data-testid="form-category" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })}
                  className="min-h-[52px] text-lg px-3 border-2 border-gray-400 rounded-lg w-full">
                  <option value="iphone">iPhone</option>
                  <option value="ipad">iPad</option>
                  <option value="windows11">Windows 11</option>
                </select>
              </div>
              <div>
                <label className="text-base font-bold block mb-1">Titel</label>
                <input data-testid="form-title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="min-h-[52px] text-lg px-3 border-2 border-gray-400 rounded-lg w-full" />
              </div>
              <div>
                <label className="text-base font-bold block mb-1">Underrubrik</label>
                <input data-testid="form-subtitle" value={form.subtitle} onChange={(e) => setForm({ ...form, subtitle: e.target.value })} className="min-h-[52px] text-lg px-3 border-2 border-gray-400 rounded-lg w-full" />
              </div>
              <div>
                <label className="text-base font-bold block mb-1">Beskrivning</label>
                <textarea data-testid="form-description" rows={4} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="text-lg px-3 py-2 border-2 border-gray-400 rounded-lg w-full" />
              </div>
              <div>
                <label className="text-base font-bold block mb-1">Innehållspunkter (en per rad)</label>
                <textarea data-testid="form-features" rows={4} value={form.features} onChange={(e) => setForm({ ...form, features: e.target.value })} className="text-lg px-3 py-2 border-2 border-gray-400 rounded-lg w-full" />
              </div>
              <div>
                <label className="text-base font-bold block mb-1">Pris (kr)</label>
                <input data-testid="form-price" type="number" value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} className="min-h-[52px] text-lg px-3 border-2 border-gray-400 rounded-lg w-40" />
              </div>

              <div>
                <label className="text-base font-bold block mb-1">PDF-fil</label>
                <label className="inline-flex items-center gap-2 px-4 py-3 border-2 border-gray-400 rounded-lg cursor-pointer hover:bg-gray-100 font-semibold">
                  {uploading === "pdf" ? <Loader2 className="animate-spin" size={20} /> : <Upload size={20} />} Ladda upp PDF
                  <input data-testid="form-pdf" type="file" accept="application/pdf" onChange={handlePdf} className="hidden" />
                </label>
                {form.pdf_filename && <p className="flex items-center gap-2 text-base text-[#0B7A3F] mt-2"><FileText size={18} /> {form.pdf_filename}</p>}
              </div>

              <div>
                <label className="text-base font-bold block mb-1">Exempelbilder</label>
                <label className="inline-flex items-center gap-2 px-4 py-3 border-2 border-gray-400 rounded-lg cursor-pointer hover:bg-gray-100 font-semibold">
                  {uploading === "img" ? <Loader2 className="animate-spin" size={20} /> : <Upload size={20} />} Ladda upp bilder
                  <input data-testid="form-images" type="file" accept="image/*" multiple onChange={handleImages} className="hidden" />
                </label>
                <div className="flex flex-wrap gap-3 mt-3">
                  {form.sample_images.map((path) => (
                    <div key={path} className="relative">
                      <img src={productImage({ sample_images: [path] })} alt="" className="w-20 h-20 object-cover rounded-lg border-2" />
                      <button onClick={() => removeImage(path)} className="absolute -top-2 -right-2 bg-[#D32F2F] text-white rounded-full p-1"><X size={14} /></button>
                    </div>
                  ))}
                </div>
              </div>

              {msg && <p className="text-[#D32F2F] font-semibold">{msg}</p>}

              <div className="flex gap-3 pt-2">
                <button data-testid="save-product" onClick={save} disabled={saving || !form.title || !form.description}
                  className="inline-flex items-center justify-center gap-2 min-h-[56px] px-6 bg-[#0056D2] text-white text-lg font-bold rounded-lg hover:bg-[#003E99] disabled:opacity-50">
                  {saving ? <Loader2 className="animate-spin" size={22} /> : null} Spara manual
                </button>
                <button onClick={() => setEditing(null)} className="min-h-[56px] px-6 border-2 border-gray-400 rounded-lg text-lg font-semibold hover:bg-gray-100">Avbryt</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
